import { useState } from 'react';
import type { WinnerDto } from '@shared/types';
import { saveBlob } from '@shared/api';
import { dateOnly, localDate, money, titleCase } from '@shared/format';
import { errorMessage, useApp, useLoad } from '../context';
import { Card, Empty, ErrorBox, Loading, TopBar } from '../components/ui';

export function Winners() {
  const { api, toast } = useApp();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState('');
  const qs = () => { const q = new URLSearchParams(); if (from) q.set('from', from); if (to) q.set('to', to); if (type) q.set('type', type); const s = q.toString(); return s ? `?${s}` : ''; };
  const { data, error, loading, reload } = useLoad(() => api.get<WinnerDto[]>(`/api/winners${qs()}`), [api, from, to, type]);

  const exportCsv = async () => {
    try { const f = await api.download(`/api/reports/winners.csv${qs()}`); saveBlob(f.blob, f.filename); toast('Winners CSV downloaded'); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };
  const total = (data ?? []).reduce((s, w) => s + w.prizeValue, 0);

  return (
    <div className="page">
      <TopBar title="Winners Report" />
      <Card>
        <div className="filters">
          <label className="field"><span>From</span><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="field"><span>To</span><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <label className="field"><span>Drawing type</span>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All drawing types</option>
              {['weekly', 'monthly', 'quarterly', 'annual'].map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </select>
          </label>
        </div>
        <div className="row-between mt">
          <button className="btn-link" onClick={() => { setFrom(''); setTo(''); setType(''); }}>All records</button>
          <button className="btn btn-primary" onClick={exportCsv}>Export CSV</button>
        </div>
      </Card>
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <Card className="table-card">
          <div className="muted small pad">{data.length} winner{data.length === 1 ? '' : 's'} • {money(total)} in prizes</div>
          {data.length === 0 ? <Empty>No winners for these filters.</Empty> : (
            <div className="table-scroll">
              <table className="table">
                <thead><tr><th>Date</th><th>Type</th><th>Employee</th><th>Employee ID</th><th>Department</th><th>Prize</th><th className="right">Value</th><th className="right">Points</th><th className="right">Entrants</th><th>Initiated by</th></tr></thead>
                <tbody>
                  {data.map((w) => (
                    <tr key={w.drawingId}>
                      <td>{localDate(w.completedAtUtc)}</td><td>{titleCase(w.drawingType)}</td><td><strong>{w.winnerName}</strong>{w.pickedByAdmin && <span className="pick-tag"> picked by admin</span>}</td>
                      <td>{w.winnerEmployeeKey}</td><td>{w.department ?? '—'}</td><td>{w.prizeName}</td>
                      <td className="right tabular">{money(w.prizeValue)}</td><td className="right tabular">{w.tokenTotalAtDrawing}</td>
                      <td className="right tabular">{w.entrantCount}</td><td>{w.initiatedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.length > 0 && <div className="muted small pad">Periods shown in CSV as PeriodStart/PeriodEnd, e.g. {dateOnly(data[0]!.periodStart)} – {dateOnly(data[0]!.periodEnd)}.</div>}
        </Card>
      )}
    </div>
  );
}
